import { useEffect, useState } from 'react';
import { PhoneFrame } from './PhoneFrame';
import './TwoPhoneHeroDemo.css';

const PHASES = [
  'idle',
  'alex-in',
  'alex-press',
  'sent',
  'syncing',
  'jordan-in',
  'jordan-press',
  'accepted',
  'toast',
  'fade',
  'reset',
] as const;

type Phase = (typeof PHASES)[number];
type Side = 'alex' | 'jordan';

const CYCLE_MS = 14100;
const SYNC_PATH = 'M 266 340 C 326 186 388 172 434 288';

const TIMELINE: ReadonlyArray<readonly [number, Phase]> = [
  [100, 'alex-in'],
  [1100, 'alex-press'],
  [1700, 'sent'],
  [2600, 'syncing'],
  [4500, 'jordan-in'],
  [6100, 'jordan-press'],
  [6800, 'accepted'],
  [8600, 'toast'],
  [12600, 'fade'],
  [13500, 'reset'],
];

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const DAYS = Array.from({ length: 30 }, (_, index) => index + 1);

const rank = (phase: Phase) => PHASES.indexOf(phase);

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

function CheckIcon() {
  return (
    <svg viewBox='0 0 12 12' fill='none' aria-hidden='true'>
      <path
        d='M2.4 6.3 4.9 8.7 9.6 3.4'
        stroke='currentColor'
        strokeWidth='1.8'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}

interface PhoneScreenProps {
  side: Side;
  cardIn: boolean;
  pressing: boolean;
  done: boolean;
  accepted: boolean;
  toast: boolean;
  veiled: boolean;
}

function PhoneScreen({
  side,
  cardIn,
  pressing,
  done,
  accepted,
  toast,
  veiled,
}: PhoneScreenProps) {
  const name = side === 'alex' ? 'Alex' : 'Jordan';
  const doneLabel =
    side === 'alex' ? 'Sent to Jordan' : 'Added for both homes';

  return (
    <div
      className={[
        'tp-screen',
        `tp-screen--${side}`,
        accepted ? 'is-accepted' : '',
        veiled ? 'is-veiled' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <header className='tp-screen__header'>
        <div className='tp-brand'>
          <span className='tp-brand__mark'>H</span>
          <span className='tp-brand__name'>Harbor</span>
        </div>
        <span className={`tp-who tp-who--${side}`}>{name}</span>
      </header>

      <div className='tp-screen__body'>
        <section className='tp-cal'>
          <div className='tp-cal__head'>
            <div>
              <p className='tp-cal__month'>November</p>
              <p className='tp-cal__year'>2026</p>
            </div>
            <span className='tp-cal__check'>
              <CheckIcon />
            </span>
          </div>

          <div className='tp-cal__week'>
            {WEEKDAYS.map((day, index) => (
              <span key={`${day}-${index}`}>{day}</span>
            ))}
          </div>

          <div className='tp-cal__grid'>
            {DAYS.map((day) => {
              const owner =
                day === 7
                  ? accepted
                    ? 'jordan'
                    : 'alex'
                  : day === 15
                    ? accepted
                      ? 'alex'
                      : 'jordan'
                    : null;

              return (
                <span
                  key={day}
                  className={[
                    'tp-day',
                    owner ? `tp-day--${owner}` : '',
                    owner && accepted ? 'is-updated' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {day}
                </span>
              );
            })}
          </div>

          <div className='tp-cal__legend'>
            <span>
              <i className='tp-legend__dot tp-legend__dot--alex' />
              Alex
            </span>
            <span>
              <i className='tp-legend__dot tp-legend__dot--jordan' />
              Jordan
            </span>
            <span className='tp-cal__legend-note'>shared</span>
          </div>
        </section>

        <section className='tp-msg'>
          <p className='tp-msg__label'>Messages</p>
          <p
            className={[
              'tp-msg__bubble',
              side === 'alex' ? 'tp-msg__bubble--out' : 'tp-msg__bubble--in',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            Can we swap Nov 7 for Nov 15?
          </p>
          <p
            className={[
              'tp-msg__bubble',
              'tp-msg__bubble--reply',
              side === 'jordan' ? 'tp-msg__bubble--out' : 'tp-msg__bubble--in',
              accepted ? 'is-shown' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            Perfect — it&apos;s on the calendar.
          </p>
        </section>

        <div className='tp-composer'>
          <span>Message {side === 'alex' ? 'Jordan' : 'Alex'}…</span>
          <span className='tp-composer__send'>↑</span>
        </div>
      </div>

      <div className={`tp-trade${cardIn ? ' is-in' : ''}`}>
        <p className='tp-trade__eyebrow'>
          <span className='tp-trade__dot' />
          Trade request
        </p>
        <p className='tp-trade__title'>
          Sat Nov 7 <span className='tp-trade__swap'>↔</span> Sun Nov 15
        </p>
        <p className='tp-trade__sub'>
          {side === 'alex'
            ? 'To Jordan · weekend swap'
            : 'From Alex · weekend swap'}
        </p>

        {done ? (
          <div className='tp-trade__done'>
            <CheckIcon />
            {doneLabel}
          </div>
        ) : side === 'alex' ? (
          <div
            className={`tp-btn tp-btn--primary${pressing ? ' is-pressing' : ''}`}
          >
            Send
          </div>
        ) : (
          <div className='tp-trade__actions'>
            <div
              className={`tp-btn tp-btn--primary${pressing ? ' is-pressing' : ''}`}
            >
              Accept
            </div>
            <div className='tp-btn tp-btn--ghost'>Decline</div>
          </div>
        )}
      </div>

      <div className={`tp-toast${toast ? ' is-in' : ''}`}>
        <span className='tp-toast__check'>
          <CheckIcon />
        </span>
        Calendar updated for both homes
      </div>

      <div className='tp-screen__veil' />
    </div>
  );
}

export function TwoPhoneHeroDemo() {
  const reduced = usePrefersReducedMotion();
  const [phase, setPhase] = useState<Phase>('idle');
  const [cycle, setCycle] = useState(0);

  useEffect(() => {
    if (reduced) return;

    setPhase('idle');
    const timers = TIMELINE.map(([delay, next]) =>
      window.setTimeout(() => setPhase(next), delay)
    );
    const loop = window.setTimeout(
      () => setCycle((value) => value + 1),
      CYCLE_MS
    );

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      window.clearTimeout(loop);
    };
  }, [cycle, reduced]);

  const effective: Phase = reduced ? 'toast' : phase === 'reset' ? 'idle' : phase;
  const reached = (target: Phase) => rank(effective) >= rank(target);

  const syncing = !reduced && phase === 'syncing';
  const accepted = reached('accepted');
  const veiled = !reduced && (phase === 'fade' || phase === 'reset');

  return (
    <div className='tp-demo'>
      <p className='tp-demo__kicker'>Two homes · One source of truth</p>

      <p className='tp-demo__hypothesis'>
        <span>What this tests:</span> whether one change can land in both homes
        at once — no forwarded screenshot, no follow-up text, no version of the
        week only one parent can see.
      </p>

      <p className='sr-only'>
        A looped demo: Alex asks Jordan to trade a weekend; Jordan accepts; both
        shared calendars update at the same moment.
      </p>

      <div className='tp-demo__stage-slot'>
        <div className='tp-demo__glow' aria-hidden='true' />

        <div
          className={`tp-demo__stage${syncing ? ' is-syncing' : ''}`}
          aria-hidden='true'
        >
          <svg className='tp-sync' viewBox='0 0 700 640' fill='none'>
            <defs>
              <linearGradient
                id='tpSyncStroke'
                x1='266'
                y1='340'
                x2='434'
                y2='288'
                gradientUnits='userSpaceOnUse'
              >
                <stop offset='0' stopColor='#0f8a8a' />
                <stop offset='1' stopColor='#018b9c' />
              </linearGradient>
            </defs>
            <path
              className='tp-sync__arc'
              d={SYNC_PATH}
              stroke='url(#tpSyncStroke)'
            />
            <path
              className='tp-sync__trail'
              d={SYNC_PATH}
              stroke='url(#tpSyncStroke)'
              pathLength={1}
            />
          </svg>

          <span className='tp-sync__ping tp-sync__ping--start' />
          <span className='tp-sync__ping tp-sync__ping--end' />
          <span className='tp-sync__dot' />

          <div className='tp-demo__phone tp-demo__phone--alex'>
            <PhoneFrame size='md' label='Alex' glow={syncing}>
              <PhoneScreen
                side='alex'
                cardIn={reached('alex-in')}
                pressing={phase === 'alex-press'}
                done={reached('sent')}
                accepted={accepted}
                toast={reached('toast')}
                veiled={veiled}
              />
            </PhoneFrame>
          </div>

          <div className='tp-demo__phone tp-demo__phone--jordan'>
            <PhoneFrame size='md' label='Jordan' glow={syncing}>
              <PhoneScreen
                side='jordan'
                cardIn={reached('jordan-in')}
                pressing={phase === 'jordan-press'}
                done={accepted}
                accepted={accepted}
                toast={reached('toast')}
                veiled={veiled}
              />
            </PhoneFrame>
          </div>
        </div>
      </div>

      <p className='tp-demo__caption'>
        One change. Both homes. <span>Always in sync.</span>
      </p>
      <p className='tp-demo__subcaption'>
        A weekend trade sent from one phone lands on the other — calendar and
        all.
      </p>
    </div>
  );
}
